import createGreenScreenFilter from "ags/filters/green-screen-filter";
import { BevelFilter, OutlineFilter } from "pixi-filters";
import { RenderTexture } from "pixi.js";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import Contract from "slotworks/utils/contracts/contract";
import Rectangle from "slotworks/utils/geom/rectangle";
import Timer from "slotworks/utils/timer";

export default class WaysHighlightComponent {

    public static LINE_THICKNESS = 6;

    protected highlightSprite: PIXI.Sprite;
    protected padding = 50;
    protected color = 0xEF0005;
    protected alpha = 1;

    protected renderTextures: PIXI.RenderTexture[] = [];
    protected sprites: PIXI.Sprite[] = [];

    constructor(symbols: SymbolComponent[], win: SymbolWinResult) {
        this.highlightSprite = this.createHighlightSprite(symbols, win);
    }

    public show(duration = 1000) {
        return new Contract<void>((resolve) => {
            this.highlightSprite.visible = true;

            Timer.setTimeout(() => {
                this.destroy();
                resolve();
            }, duration);
        });
    }

    public destroy() {

        this.sprites.forEach((sprite) => sprite.destroy());
        this.renderTextures.forEach((texture) => texture.destroy(true));

        this.sprites = [];
        this.renderTextures = [];
    }

    protected createHighlightSprite(symbols: SymbolComponent[], win: SymbolWinResult) {
        const highlightSprite = this.createOutline(symbols, win);

        const animationLayer = symbols[0].getAnimationLayer();
        animationLayer.add(highlightSprite);

        highlightSprite.visible = false;

        return highlightSprite;
    }

    protected createOutline(symbols: SymbolComponent[], win: SymbolWinResult) {
        const symbolOutline = this.createSymbolOutline(symbols, win);

        const matrixLayer = symbols[0].getMatrixLayer();
        const renderer = canvasManager.renderer;

        const border = graphicsManager.createSprite("win_over");
        const borderPosition = matrixLayer.getPosition("win_over");
        border.width = borderPosition.width;
        border.height = borderPosition.height;

        const matrixBounds = matrixLayer.bounds;
        const borderTexture = this.createRenderTexture(matrixBounds);

        // We render the border outline to prevent the win borders getting affected by the filters.
        renderer.render(symbolOutline, borderTexture, false);

        // Add win borders to each symbol.
        symbols.forEach((symbol) => {
            const gridPosition = symbol.gridPosition;
            const position = matrixLayer.getPosition(`symbol_${gridPosition.x}_${gridPosition.y}`);

            border.x = position.x + this.padding / 2;
            border.y = position.y + this.padding / 2;
            renderer.render(border, borderTexture, false);
        });

        const borderSprite = this.createSprite(borderTexture);

        borderSprite.x -= this.padding / 2;
        borderSprite.y -= this.padding / 2;

        return borderSprite;
    }

    protected createSymbolOutline(symbols: SymbolComponent[], win: SymbolWinResult) {

        const outlineTexture = this.createOutlineTexture(symbols, win);
        const symbolOutline = this.createSprite(outlineTexture);

        symbolOutline.filters = this.getFilters();

        return symbolOutline;
    }

    protected createOutlineTexture(symbols: SymbolComponent[], win: SymbolWinResult, outlineTexture?: RenderTexture) {
        const matrixLayer = symbols[0].getMatrixLayer();
        const matrixBounds = matrixLayer.bounds;
        outlineTexture = outlineTexture || this.createRenderTexture(matrixBounds);

        const renderer = canvasManager.renderer;
        const cutoutGraphic = this.createGreensSreenGraphic(matrixLayer);

        symbols.forEach((symbol) => {
            const gridPosition = symbol.gridPosition;
            const position = matrixLayer.getPosition(`symbol_${gridPosition.x}_${gridPosition.y}`);
            cutoutGraphic.x = position.x + this.padding / 2;
            cutoutGraphic.y = position.y + this.padding / 2;
            renderer.render(cutoutGraphic, outlineTexture, false);
        });

        return outlineTexture;
    }

    protected createRenderTexture(bounds: Rectangle) {
        const renderTexture = PIXI.RenderTexture.create(bounds.width + this.padding, bounds.height + this.padding);

        this.renderTextures.push(renderTexture);

        return renderTexture;
    }

    protected createSprite(texture: PIXI.Texture) {
        const sprite = new PIXI.Sprite(texture);

        this.sprites.push(sprite);

        return sprite;
    }

    protected getFilters() {
        const outlineFilter = new OutlineFilter(WaysHighlightComponent.LINE_THICKNESS, 0xEF0005, 1);
        const greenscreenFilter = createGreenScreenFilter();

        const bevelFilter = new BevelFilter({
            rotation: 54,
            thickness: Math.round(WaysHighlightComponent.LINE_THICKNESS / 2),
            lightColor: 0xe70000,
            lightAlpha: 0.7,
            shadowColor: 0xa70a00,
            shadowAlpha: 0.7
        });

        return [outlineFilter, greenscreenFilter, bevelFilter];
    }

    protected createGreensSreenGraphic(matrixLayer: Layers) {
        const borderPosition = matrixLayer.getPosition(`win_over`);
        const greenScreen = new PIXI.Graphics();
        greenScreen.beginFill(0x00ff00, 1);
        greenScreen.drawRect(0, 0, 1, 1);
        greenScreen.endFill();
        greenScreen.x = borderPosition.x;
        greenScreen.y = borderPosition.y;
        greenScreen.width = borderPosition.width;
        greenScreen.height = borderPosition.height;

        return greenScreen;
    }
}
